import Papa from "papaparse";
import { cleanHandle } from "@/lib/utils/csvParsing";
import { SAMPLE_CSV_DATA } from "../constants";
import type { ParsedInfluencer, RowValidationResult, ValidationResult } from "../types";

export function convertFollowersToNumber(followers: string | number | undefined | null): number {
  if (followers === undefined || followers === null) return 0;
  if (typeof followers === 'number') return followers || 0;

  const str = followers.toString().toLowerCase().trim();
  const num = parseFloat(str);

  if (str.includes('m')) return Math.round(num * 1000000);
  if (str.includes('k')) return Math.round(num * 1000);
  return Math.round(num) || 0;
}

export function validateRow(
  row: Record<string, string | undefined>,
  _rowIndex: number
): RowValidationResult {
  const errors: string[] = [];

  // Required fields validation
  if (!row.handle?.trim()) errors.push('Handle is required');
  // Platform validation
  if (row.platform && !['instagram', 'youtube'].includes(row.platform.toLowerCase())) {
    errors.push('Platform must be either "instagram" or "youtube"');
  }

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  // Parse and clean data
  const handle = cleanHandle(row.handle || '');
  const influencer: ParsedInfluencer = {
    name: row.name?.trim() || handle, // Fallback to handle
    handle,
    platform: row.platform ? row.platform.toLowerCase() : 'instagram',
    followers_count: row.followers_count ? convertFollowersToNumber(row.followers_count) : undefined,
    engagement_rate: row.engagement_rate ? parseFloat(row.engagement_rate) : undefined,
    location: row.location?.trim() || undefined,
    email: row.email?.trim() || undefined,
    phone: row.phone?.trim() || undefined,
    categories: row.tags ? row.tags.split('|').map((tag: string) => tag.trim()).filter(Boolean) : undefined,
  };

  return { valid: true, influencer, errors: [] };
}

export function downloadSampleCSV(): void {
  const blob = new Blob([SAMPLE_CSV_DATA], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'influencers_sample.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function parseInfluencerCsvFile(
  file: File,
  onComplete: (data: { headers: string[]; previewData: Record<string, unknown>[]; validation: ValidationResult }) => void,
  onError: (error: Error) => void
): void {
  Papa.parse<Record<string, string>>(file, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => {
      const lowerHeader = header.trim().toLowerCase();
      if (
        lowerHeader.includes('handle') ||
        lowerHeader.includes('handel') ||
        lowerHeader.includes('username') ||
        lowerHeader.includes('instagram') ||
        lowerHeader === 'ig'
      ) {
        return 'handle';
      }
      return lowerHeader;
    },
    complete: (results) => {
      const data = results.data;
      const headers = Object.keys(data[0] || {});
      const previewData = data.slice(0, 5) as Record<string, unknown>[];

      const validation: ValidationResult = { valid: [], invalid: [] };

      data.forEach((row, index) => {
        const result = validateRow(row, index);
        if (result.valid && result.influencer) {
          validation.valid.push(result.influencer);
        } else {
          validation.invalid.push({
            row: index + 1,
            errors: result.errors,
            data: row,
          });
        }
      });

      onComplete({ headers, previewData, validation });
    },
    error: (error) => {
      onError(error);
    }
  });
}
